import { type CategorySizeMapping } from '@/modules/categories/aliases';
import { type SizeCountryNames } from '@/modules/categories/types';
import { type SizeFiltersResponse } from '@/modules/filters/aliases';
import { EU_LOCALES, SupportedLocation } from '@/modules/location/constants';
import { isSupportedLocation } from '@/modules/location/helpers';
import { flattenSizeTree } from '@/modules/sizes/helpers';

/**
 * @param sizeId - E.g. 54 is equivalent to US/Menswear/Tops
 * @param sizesData - Data from useSizeFilters() hook
 * @returns the size array for the given sizeId to be used in dropdowns.
 */
export function getLocalisedSizes(
  sizeId?: number,
  sizesData?: SizeFiltersResponse
) {
  if (!sizeId || !sizesData?.length) {
    return [];
  }

  // Flatten the size tree to find all the branch nodes
  const flattenedTree = flattenSizeTree(sizesData);

  const sizeByLocale = flattenedTree?.find(({ id }) => id === sizeId);

  return (
    sizeByLocale?.children.map(({ composite_id, name }) => ({
      value: composite_id,
      label: name ?? '',
    })) ?? []
  );
}

/**
 * Maps location to the country code used in composite size IDs (e.g., '2-56.1-EUR')
 * This matches the format used in size filter data from the API.
 */
export function getSizeLocaleName(location?: string): SizeCountryNames {
  if (!location) {
    return 'US'; // default to US
  }

  if (isSupportedLocation(location)) {
    if (EU_LOCALES.includes(location)) {
      return 'EUR';
    }
    if (location === SupportedLocation.GB) {
      return 'UK';
    }
    if (location === SupportedLocation.AU) {
      return 'AU';
    }
  }
  return 'US';
}

export function getSizeId({
  categorySizeMap,
  department,
  group,
  productType,
  location,
}: {
  categorySizeMap?: CategorySizeMapping[];
  department: string;
  group?: string;
  productType?: string;
  location: SupportedLocation;
}) {
  if (!categorySizeMap || !productType) {
    return;
  }

  const sizeSet = categorySizeMap.find((item) => {
    const groupMatch = group ? item.group === group : true;

    return (
      item.department === department &&
      groupMatch &&
      item.product_type === productType
    );
  })?.size_set_by_region;

  // Map location to API region key (IT for EU, GB for UK, etc.)
  let region: 'IT' | 'GB' | 'US' | 'AU' = 'US';
  if (location && isSupportedLocation(location)) {
    if (EU_LOCALES.includes(location)) {
      region = 'IT';
    } else if (location === SupportedLocation.GB) {
      region = 'GB';
    } else if (location === SupportedLocation.AU) {
      region = 'AU';
    } else {
      region = 'US';
    }
  }

  return sizeSet?.[region];
}
